import { Component, computed, ElementRef, inject, input, NgZone, OnDestroy, signal, ViewEncapsulation } from '@angular/core';
import { Subscription } from 'rxjs';
import { MDIconComponent } from '@melodic-ui/components';
import { AnalyticsEvent, AnalyticsService } from '@lb/services';
import { LoaderComponent } from '../../loader/loader.component';
import { AudioTranscribeService, LiveTranscriptionSession } from '../audio-transcribe.service';
import { BASE_SPEECH_LOCALE, getRecognitionLocales } from '../speech-locales';

type RecordingState = 'idle' | 'requesting' | 'recording' | 'error';

const MAX_SECONDS = 60;

/**
 * Wraps any textarea with a microphone button overlay in the top-right corner.
 * Clicking the mic starts a live Azure Speech session: transcribed text appears in the
 * textarea in real time, appended after whatever was there when recording began. While
 * recording, the mic becomes a stop button next to a count-up timer (capped at 1:00).
 *
 * Live text is written straight into the projected <textarea> and an `input` event is
 * dispatched, so parents keep their existing `(input)` binding — no extra wiring needed.
 *
 * Pass `languageID` (the lesson's teaching language) to recognize both English and that language
 * within a single recording — see `speech-locales.ts`. Without it, recognition is English only.
 *
 * Usage:
 *   <lb-audio-transcribe-input [languageID]="lesson()?.languageID ?? null">
 *     <textarea [value]="text()" (input)="onChange($any($event.target).value)"></textarea>
 *   </lb-audio-transcribe-input>
 */
@Component({
	selector: 'lb-audio-transcribe-input',
	standalone: true,
	imports: [MDIconComponent, LoaderComponent],
	templateUrl: './audio-transcribe-input.component.html',
	styleUrl: './audio-transcribe-input.component.scss',
	encapsulation: ViewEncapsulation.None
})
export class AudioTranscribeInputComponent implements OnDestroy {
	private readonly _host = inject(ElementRef<HTMLElement>);
	private readonly _zone = inject(NgZone);
	private readonly _transcribeService = inject(AudioTranscribeService);
	private readonly _analytics = inject(AnalyticsService);

	/** BCP-47 tag for the speaker's own language — always a recognition candidate. */
	readonly language = input<string>(BASE_SPEECH_LOCALE);

	/**
	 * LanguageID of the language being taught. When it maps to a locale other than `language`,
	 * recognition auto-detects between the two so the instructor can dictate in either.
	 */
	readonly languageID = input<number | null>(null);

	/**
	 * Disables the mic alongside the wrapped textarea. Recording writes straight into that textarea,
	 * so a disabled field with a live mic would take dictation the user cannot otherwise edit.
	 */
	readonly disabled = input<boolean>(false);

	/** Recognition candidates: `language`, plus the taught language when it differs. */
	private readonly _locales = computed(() => getRecognitionLocales(this.languageID(), this.language()));

	readonly state = signal<RecordingState>('idle');
	readonly errorMessage = signal<string | null>(null);
	readonly elapsedSeconds = signal<number>(0);
	readonly hasReceivedAudio = signal<boolean>(false);

	private _session: LiveTranscriptionSession | null = null;
	private _subscriptions: Subscription[] = [];
	private _timerHandle: ReturnType<typeof setInterval> | null = null;
	/** Whether this instance currently holds the global recording lock. */
	private _ownsLock = false;

	/** Text in the textarea when recording started — live transcript is appended after it. */
	private _baseText = '';
	/** Finalized segments joined with spaces. */
	private _finalizedText = '';
	/** Current in-progress hypothesis (replaced as the user keeps speaking). */
	private _interimText = '';

	/** True when a different recorder holds the global lock — disables this mic button. */
	readonly blockedByOther = computed(() => this._transcribeService.isRecording() && !this._ownsLock);

	get isRecording(): boolean {
		return this.state() === 'recording';
	}

	get isRequesting(): boolean {
		// Also covers the warmup period while recording but before Azure sends the first result.
		return this.state() === 'requesting' || (this.state() === 'recording' && !this.hasReceivedAudio());
	}

	get formattedTime(): string {
		return this._format(this.elapsedSeconds());
	}

	get formattedMax(): string {
		return this._format(MAX_SECONDS);
	}

	toggle(): void {
		if (this.isRecording) {
			void this.stop();
		} else if (!this.disabled() && this.state() !== 'requesting') {
			this.start();
		}
	}

	/**
	 * Warms the Azure token ahead of the click (on hover/focus) so the function cold-start and
	 * STS hop happen during idle time. By the time the user clicks the mic, the token is cached
	 * and recording starts immediately. No-ops while a token is already fresh.
	 */
	prewarm(): void {
		if (this.state() === 'idle' || this.state() === 'error') {
			this._transcribeService.prewarm();
		}
	}

	start(): void {
		const textarea = this._textarea();
		if (!textarea) {
			return;
		}

		// Claim the single global recording slot before touching any state. This runs
		// synchronously, so a second mic click during another recorder's warmup is rejected.
		if (!this._transcribeService.acquireRecordingLock()) {
			return;
		}
		this._ownsLock = true;

		const locales = this._locales();
		this._analytics.trackEvent(AnalyticsEvent.VoiceRecordStarted, { language: locales.join(',') });

		this.state.set('requesting');
		this.errorMessage.set(null);
		this._baseText = textarea.value;
		this._finalizedText = '';
		this._interimText = '';

		this._subscriptions.push(
			this._transcribeService.startLiveTranscription(locales).subscribe({
				next: (session) => this._onSessionStarted(session),
				error: (err) => {
					console.error('[AudioTranscribe] startLiveTranscription failed:', err);
					this._fail('Could not start recording. Check your microphone and try again.');
				}
			})
		);
	}

	async stop(): Promise<void> {
		this._stopTimer();
		// Unsubscribe first so that a `recognized` event arriving during session teardown
		// can't append the same words we're about to commit from _interimText (double text).
		this._clearSubscriptions();
		const session = this._session;
		this._session = null;

		// Commit whatever interim text remains so it isn't lost on stop.
		if (this._interimText) {
			this._appendFinalized(this._interimText);
			this._interimText = '';
			this._writeToTextarea();
		}

		this.state.set('idle');
		this.elapsedSeconds.set(0);
		this._releaseLock();
		await session?.stop();
	}

	private _onSessionStarted(session: LiveTranscriptionSession): void {
		this._session = session;
		this.state.set('recording');
		this.elapsedSeconds.set(0);
		this.hasReceivedAudio.set(false);

		this._subscriptions.push(
			// sessionStarted fires when the Azure WebSocket handshake completes — use it to
			// drop the spinner and show the live stop button + timer without requiring speech.
			session.connected$.subscribe(() => this._zone.run(() => {
				this.hasReceivedAudio.set(true);
				this._startTimer();
			})),
			// SDK callbacks fire outside Angular's zone — run() re-enters it so signals
			// and [value] bindings update immediately on each recognized chunk.
			session.interim$.subscribe((text) => this._zone.run(() => {
				this._interimText = text;
				this._writeToTextarea();
			})),
			session.finalized$.subscribe((text) => this._zone.run(() => {
				this._appendFinalized(text);
				this._interimText = '';
				this._writeToTextarea();
			})),
			session.error$.subscribe((message) => this._zone.run(() => this._fail(message)))
		);
	}

	private _startTimer(): void {
		this._timerHandle = setInterval(() => {
			const next = this.elapsedSeconds() + 1;
			this.elapsedSeconds.set(next);
			if (next >= MAX_SECONDS) {
				void this.stop();
			}
		}, 1000);
	}

	private _appendFinalized(segment: string): void {
		this._finalizedText = this._finalizedText ? `${this._finalizedText} ${segment}` : segment;
	}

	private _writeToTextarea(): void {
		const textarea = this._textarea();
		if (!textarea) {
			return;
		}

		const transcript = [this._finalizedText, this._interimText].filter(Boolean).join(' ');
		const value = this._baseText && transcript ? `${this._baseText} ${transcript}` : this._baseText || transcript;

		textarea.value = value;
		// Notify the parent's (input) binding so its model/signal stays in sync.
		textarea.dispatchEvent(new Event('input', { bubbles: true }));
	}

	private _fail(message: string): void {
		this._stopTimer();
		void this._session?.stop();
		this._session = null;
		this._clearSubscriptions();
		this._releaseLock();
		this.state.set('error');
		this.errorMessage.set(message);
		this.elapsedSeconds.set(0);
	}

	private _releaseLock(): void {
		if (this._ownsLock) {
			this._transcribeService.releaseRecordingLock();
			this._ownsLock = false;
		}
	}

	private _textarea(): HTMLTextAreaElement | null {
		return (this._host.nativeElement as HTMLElement).querySelector('textarea');
	}

	private _format(totalSeconds: number): string {
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;
		return `${minutes}:${String(seconds).padStart(2, '0')}`;
	}

	private _stopTimer(): void {
		if (this._timerHandle !== null) {
			clearInterval(this._timerHandle);
			this._timerHandle = null;
		}
	}

	private _clearSubscriptions(): void {
		this._subscriptions.forEach((sub) => sub.unsubscribe());
		this._subscriptions = [];
	}

	ngOnDestroy(): void {
		this._stopTimer();
		void this._session?.stop();
		this._clearSubscriptions();
		this._releaseLock();
	}
}
